import React, { useEffect } from "react";

import type { Hooks, SDKResult } from "@loomhq/loom-sdk";
import { captureMessage } from "@sentry/browser";

type Loom = {
	support: "full" | "blocked" | "unsupported";
	instance: SDKResult | null;
};

let isRecording = false;
let loom: Loom | "loading" | undefined = undefined;

const RERENDER_EVENT_TYPE = "videoRecording:useManualRerender";
const rerenderEvent = new Event(RERENDER_EVENT_TYPE);
function rerender() {
	window.dispatchEvent(rerenderEvent);
}

export async function setupLoom() {
	// A loading state is needed to prevent multiple requests from going out
	loom = "loading";
	rerender();

	const { isSupported, setup } = await import("@loomhq/loom-sdk");
	const { error } = await isSupported();

	switch (error) {
		case "incompatible-browser":
		case "no-media-streams-support":
			loom = {
				support: "unsupported",
				instance: null,
			};

			return rerender();
		case "third-party-cookies-disabled":
			loom = {
				support: "blocked",
				instance: null,
			};

			return rerender();
		default: {
			const instance = await setup({ apiKey: window.LOOM_RECORD_API_KEY });
			const { success } = instance.status();

			if (!success) {
				captureMessage("Loom initialization was not successful.");

				loom = {
					support: "full",
					instance: null,
				};

				return rerender();
			} else {
				const newLoom: SDKResult = {
					...instance,
					configureButton: params => {
						if (!params) return instance.configureButton();

						const { element, hooks } = params;
						if (!hooks) return instance.configureButton({ element });

						const newHooks: Hooks = {
							...hooks,
							onRecordingStarted: () => {
								isRecording = true;
								rerender();
								hooks.onRecordingStarted?.();
							},
							onCancel: () => {
								isRecording = false;
								rerender();
								hooks.onCancel?.();
							},
							onRecordingComplete: loomVideo => {
								isRecording = false;
								rerender();
								hooks.onRecordingComplete?.(loomVideo);
							},
						};
						return instance.configureButton({ element, hooks: newHooks });
					},
				};

				loom = {
					support: "full",
					instance: newLoom,
				};

				return rerender();
			}
		}
	}
}

function useManualRerender() {
	const [, setKey] = React.useState(0);

	useEffect(() => {
		function modifyReactState() {
			setKey(n => n + 1);
		}

		window.addEventListener(RERENDER_EVENT_TYPE, modifyReactState);

		return () => {
			window.removeEventListener(RERENDER_EVENT_TYPE, modifyReactState);
		};
	});
}

export function useRecordingState() {
	useManualRerender();

	return isRecording;
}

export function useVideoRecording() {
	useManualRerender();

	useEffect(() => {
		if (loom === undefined) setupLoom();
	}, []);

	return loom;
}
